import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/DoctorSalesDetails.css'; 
import { format } from 'date-fns';
import logo from '../assets/logo192.png';
import SalesView from './SalesView';

/**
 * DoctorSalesDetails Component
 * 
 * Features implemented:
 * 1. Procedure Category: Added 'Procedure' category that maps to items with category 'Procedure'
 * 2. 10% Reduction: Applied to all categories except Consultation, Surgery, and Procedure before commission calculation
 * 3. Echocardiogram Reassignment: All echocardiogram sales go to designated doctor (configurable via REACT_APP_ECHOCARDIOGRAM_DOCTOR env variable)
 * 4. Customer-Specific Adjustments: Additional reductions based on customer's 'CF.Adjustment Percent' and 'CF.GST Tax Percent' fields
 * 
 * Configuration:
 * - Set REACT_APP_ECHOCARDIOGRAM_DOCTOR environment variable to specify which doctor receives all echocardiogram commissions
 * - Customer adjustments are automatically applied from the customers table based on sales_headers.customer_name
 * - Multiple reductions stack: 10% base reduction + customer adjustment percent + customer GST percent
 */

const DoctorSalesDetails = () => {
  const [salesDetails, setSalesDetails] = useState([]);
  const [commissionRates, setCommissionRates] = useState({});
  const [echocardiogramDoctor, setEchocardiogramDoctor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [selectedSalesNumber, setSelectedSalesNumber] = useState(null);
  const { hasPermission } = useAuth();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const doctorName = queryParams.get('doctor');
  const startDate = queryParams.get('startDate');
  const endDate = queryParams.get('endDate');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!doctorName) return;
      
      setIsLoading(true);
      try {
        // Fetch both sales details and commission rates in parallel
        const [salesResponse, commissionResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_DOCTOR_SALES_DETAILS}`, {
            params: {
              doctorName: doctorName,
              startDate: startDate,
              endDate: endDate,
            },
          }),
          axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/doctor-commission-configs/by-name/${encodeURIComponent(doctorName)}`)
        ]);

        // Check if there's a designated doctor for echocardiogram commissions
        // You can configure this via environment variable or make it a system setting
        const echoDoctor = process.env.REACT_APP_ECHOCARDIOGRAM_DOCTOR;
        if (echoDoctor && echoDoctor.trim() !== '') {
          setEchocardiogramDoctor(echoDoctor.trim());
        }
        
        setSalesDetails(salesResponse.data);
        
        // Convert commission configs to a lookup object
        const ratesLookup = {};
        if (commissionResponse.data && commissionResponse.data.commission_rates) {
          commissionResponse.data.commission_rates.forEach(rate => {
            ratesLookup[rate.category.toLowerCase()] = rate.rate / 100; // Convert percentage to decimal
          });
        }
        setCommissionRates(ratesLookup);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set default rates if commission config fetch fails
        setCommissionRates({
          consultation: 0.5,
          observation: 0.1,
          surgery: 1.0,
          procedure: 0.3,
          ultrasound: 0.5,
          lab: 0.1,
          medication: 0.1,
          radiology: 0.1,
          admission: 0.3,
          echocardiogram: 0.4
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [doctorName, startDate, endDate]);

  const formatDateRange = (start, end) => {
    const formattedStart = format(new Date(start), 'MMM dd yyyy');
    const formattedEnd = format(new Date(end), 'MMM dd yyyy');
    return `${formattedStart} - ${formattedEnd}`;
  };

  // Helper function to apply reductions: existing 10% + customer-specific adjustments
  const applyReduction = (amount, category, customerAdjustmentPercent = 0, customerGstPercent = 0) => {
    const exemptCategories = ['consultation', 'surgery', 'procedure','echocardiogram'];
    let reducedAmount = amount;
    
    // Apply existing 10% reduction for non-exempt categories
    if (!exemptCategories.includes(category.toLowerCase())) {
      reducedAmount = reducedAmount * 0.9;
    }
    
    // Apply customer adjustment percentage (if provided)
    if (customerAdjustmentPercent > 0) {
      reducedAmount = reducedAmount * (1 - customerAdjustmentPercent / 100);
    }
    
    // Apply customer GST tax percentage (if provided)  
    if (customerGstPercent > 0) {
      reducedAmount = reducedAmount * (1 - customerGstPercent / 100);
    }
    
    return reducedAmount;
  };

  const calculateEarnings = (row) => {
    // Handle echocardiogram reassignment logic:
    // 1. If no designated echo doctor is configured: treating doctors get echo from their own invoices
    // 2. If designated echo doctor is configured: designated doctor gets ALL echo commissions  
    // 3. If treating doctor is same as designated doctor: they get echo + all other commissions
    // 4. Edge case: if doctor name matches invoice doctor, include echo (handles designated + treating scenario)
    const shouldIncludeEcho = !echocardiogramDoctor || 
                             doctorName === echocardiogramDoctor ||
                             (row.doctor_name === doctorName);
    
    // Get customer adjustment percentages from the row data
    const customerAdjustment = parseFloat(row.customer_adjustment_percent || 0);
    const customerGst = parseFloat(row.customer_gst_percent || 0);
    
    const consultations = applyReduction(parseFloat(row.Consultation || 0), 'consultation', customerAdjustment, customerGst) * (commissionRates.consultation || 0);
    const observations = applyReduction(parseFloat(row.Observation || 0), 'observation', customerAdjustment, customerGst) * (commissionRates.observation || 0);
    const surgeries = applyReduction(parseFloat(row.Surgery || 0), 'surgery', customerAdjustment, customerGst) * (commissionRates.surgery || 0);
    const procedures = applyReduction(parseFloat(row.ProcedureTotal || 0), 'procedure', customerAdjustment, customerGst) * (commissionRates.procedure || 0);
    const ultrasound = applyReduction(parseFloat(row.Ultrasound || 0), 'ultrasound', customerAdjustment, customerGst) * (commissionRates.ultrasound || 0);
    const labs = applyReduction(parseFloat(row.Lab || 0), 'lab', customerAdjustment, customerGst) * (commissionRates.lab || 0);
    const medications = applyReduction(parseFloat(row.Medication || 0), 'medication', customerAdjustment, customerGst) * (commissionRates.medication || 0);
    const radiology = applyReduction(parseFloat(row.Radiology || 0), 'radiology', customerAdjustment, customerGst) * (commissionRates.radiology || 0);
    const admission = applyReduction(parseFloat(row.Admission || 0), 'admission', customerAdjustment, customerGst) * (commissionRates.admission || 0);
    const echocardiogram = shouldIncludeEcho ? applyReduction(parseFloat(row.Echocardiogram || 0), 'echocardiogram', customerAdjustment, customerGst) * (commissionRates.echocardiogram || 0) : 0;
    
    // Debug: Log when echocardiogram amount exists
    /*
    if (parseFloat(row.Echocardiogram || 0) > 0) {
      console.log('Echo Debug:', {
        echoAmount: row.Echocardiogram,
        shouldIncludeEcho,
        echoRate: commissionRates.echocardiogram,
        echoCommission: echocardiogram,
        doctorName,
        echocardiogramDoctor,
        rowDoctorName: row.doctor_name
      });
    }
    */
    return consultations + observations + surgeries + procedures + ultrasound + labs + medications + radiology + admission + echocardiogram;
  };

  const calculateGrandTotal = () => {
    return salesDetails.reduce((acc, row) => acc + calculateEarnings(row), 0).toFixed(2);
  };

  const getEarningsTableData = () => {
    const allCategories = [
      { 
        key: 'consultation',
        label: 'Consultations', 
        dataField: 'Consultation'
      },
      { 
        key: 'observation',
        label: 'Observations', 
        dataField: 'Observation'
      },
      { 
        key: 'surgery',
        label: 'Surgeon Fees', 
        dataField: 'Surgery'
      },
      { 
        key: 'procedure',
        label: 'Procedure', 
        dataField: 'ProcedureTotal'
      },
      { 
        key: 'ultrasound',
        label: 'Ultrasound', 
        dataField: 'Ultrasound'
      },
      { 
        key: 'lab',
        label: 'Lab', 
        dataField: 'Lab'
      },
      { 
        key: 'medication',
        label: 'Medication', 
        dataField: 'Medication'
      },
      { 
        key: 'radiology',
        label: 'Radiology', 
        dataField: 'Radiology'
      },
      { 
        key: 'admission',
        label: 'Admission', 
        dataField: 'Admission'
      },
      { 
        key: 'echocardiogram',
        label: 'Echocardiogram', 
        dataField: 'Echocardiogram'
      }
    ];

    // Filter categories where commission rate exists and is greater than 0
    return allCategories
      .filter(category => commissionRates[category.key] && commissionRates[category.key] > 0)
      .map(category => {
        // For echocardiogram category, only show if this doctor should receive those commissions
        // Show echocardiogram category if: no designated doctor OR this doctor is the designated doctor
        const shouldIncludeCategory = category.key !== 'echocardiogram' || 
                                     !echocardiogramDoctor || 
                                     doctorName === echocardiogramDoctor;
        
        if (!shouldIncludeCategory) {
          return null;
        }
        
        return {
          label: category.label,
          percentage: ((commissionRates[category.key] || 0) * 100).toFixed(1),
          earnings: salesDetails.reduce((acc, row) => {
            let amount = parseFloat(row[category.dataField] || 0);
            
            // For echocardiogram, include ALL echocardiogram sales if this doctor is designated
            if (category.key === 'echocardiogram' && echocardiogramDoctor && doctorName === echocardiogramDoctor) {
              // Already handled by the backend query
            } else if (category.key === 'echocardiogram' && echocardiogramDoctor && doctorName !== echocardiogramDoctor) {
              // This doctor should not get echocardiogram commissions
              amount = 0;
            }
            
            // Get customer adjustment percentages from the row data
            const customerAdjustment = parseFloat(row.customer_adjustment_percent || 0);
            const customerGst = parseFloat(row.customer_gst_percent || 0);
            
            const reducedAmount = applyReduction(amount, category.key, customerAdjustment, customerGst);
            return acc + (reducedAmount * (commissionRates[category.key] || 0));
          }, 0).toFixed(2)
        };
      })
      .filter(item => item !== null);
  };

  const earningsTableData = getEarningsTableData();
const dataColumnWdith = '90px';

  const handleSalesNumberClick = (salesNumber) => {
    if (hasPermission('/patient-card/Tabs/Billing', 'View')) {
      setSelectedSalesNumber(salesNumber);
      setSalesModalOpen(true);
    }
  };

  const columns = [
    { 
      name: 'Sales #', 
      selector: row => row.sales_number, 
      sortable: true,
      minWidth: '120px',
      maxWidth: '150px',
      cell: row => (
        hasPermission('/patient-card/Tabs/Billing', 'View') ? (
          <span 
            style={{ 
              color: '#007bff', 
              cursor: 'pointer', 
              textDecoration: 'underline'
            }}
            className="sales-number-link"
            onClick={() => handleSalesNumberClick(row.sales_number)}
          >
            {row.sales_number}
          </span>
        ) : (
          <span>{row.sales_number}</span>
        )
      )
    },
    { 
      name: 'Patient', 
      selector: row => row.patient_name, 
      sortable: true,
      minWidth: '200px',
      maxWidth: '300px',
    },
    { 
      name: 'Date', 
      selector: row => row.sales_date, 
      sortable: true,
      minWidth: '120px',
      maxWidth: '140px',
    },
    { 
      name: 'Commission Total', 
      selector: row => calculateEarnings(row).toFixed(2), 
      sortable: true,
      minWidth: '150px',
      maxWidth: '200px',
      style: {
        fontWeight: 'bold'
      }
    }
  ];

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/doctor-sales-summary');
  };

  // Helper function to get unique customer adjustments summary
  const getCustomerAdjustmentsSummary = () => {
    const adjustments = new Set();
    salesDetails.forEach(row => {
      const customerAdj = parseFloat(row.customer_adjustment_percent || 0);
      const customerGst = parseFloat(row.customer_gst_percent || 0);
      if (customerAdj > 0 || customerGst > 0) {
        let adjText = [];
        if (customerAdj > 0) adjText.push(`${customerAdj}% adjustment`);
        if (customerGst > 0) adjText.push(`${customerGst}% GST reduction`);
        adjustments.add(adjText.join(' + '));
      }
    });
    return Array.from(adjustments);
  };

  return (
    <div className="doctor-sales-details">
      <div className="report-header">
        <img src={logo} alt="Logo" className="report-logo" />
        <button onClick={handleBack} className="close-doctor-sales-button">X</button>
      </div>

      <div className="report-title">
        <h2>Doctor Patient Visit Details</h2>
      </div>

      <div className="header-container">
        <table className="doctor-info-table">
          <tbody>
            <tr>
              <td><strong>Doctor:</strong></td>
              <td>{doctorName}</td>
            </tr>
            <tr>
              <td><strong>Pay Period:</strong></td>
              <td>{formatDateRange(startDate, endDate)}</td>
            </tr>
            {echocardiogramDoctor && doctorName !== echocardiogramDoctor && (
              <tr className="no-print">
                <td><strong>Note:</strong></td>
                <td style={{ color: '#d63384', fontSize: '0.9em' }}>
                  Echocardiogram commissions are assigned to {echocardiogramDoctor}
                </td>
              </tr>
            )}
            {echocardiogramDoctor && doctorName === echocardiogramDoctor && (
              <tr className="no-print">
                <td><strong>Note:</strong></td>
                <td style={{ color: '#198754', fontSize: '0.9em' }}>
                  This doctor receives all echocardiogram commissions
                </td>
              </tr>
            )}
            {getCustomerAdjustmentsSummary().length > 0 && (
              <tr className="no-print">
                <td><strong>Customer Adjustments:</strong></td>
                <td style={{ color: '#fd7e14', fontSize: '0.9em' }}>
                  {getCustomerAdjustmentsSummary().join(', ')} applied to applicable categories
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <table className="earnings-summary-table">
          <thead>
            <tr>
              <th>Total Type</th>
              <th>Commission %</th>
              <th>Earnings</th>
            </tr>
          </thead>
          <tbody>
            {earningsTableData.map((row, index) => (
              <tr key={index}>
                <td>{row.label}</td>
                <td>{row.percentage}%</td>
                <td>{row.earnings}</td>
              </tr>
            ))}
            <tr>
              <td colSpan="2"><strong>Total Earnings</strong></td>
              <td><strong>{calculateGrandTotal()}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="data-table-container">
        <DataTable
          columns={columns}
          data={salesDetails.filter(row => calculateEarnings(row) > 0)}
          highlightOnHover
          progressPending={isLoading}
          pagination={false} 
        />
      </div>

      <button onClick={handlePrint} className="btn btn-primary print-button">
        Print
      </button>

      {/* Sales Modal */}
      {salesModalOpen && selectedSalesNumber && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <button 
              className="modal-close" 
              onClick={() => setSalesModalOpen(false)}
            >
              ×
            </button>
            <SalesView salesNumber={selectedSalesNumber} />
          </div>
        </div>
      )}

      {/* Print styles */}
      <style jsx>{`
        @media print {
          .sales-number-link {
            color: black !important;
            text-decoration: none !important;
            cursor: default !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DoctorSalesDetails;
